import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Contributor } from "../components/Contributor";
import { Typography } from "antd";

interface ContributorData {
  name: string;
  activity_summary: string;
  impact_area: string;
  top_items: string[];
  stats: {
    metric: string;
    value: number;
    evidence: string[];
  }[];
}

// NotableContributors Block Recipe
const notableContributorsRecipe = {
  id: "notable-contributors-block",
  name: "Notable Contributors Block",
  globalKey: [],
  prefix: `[
        {
          "template": "string — e.g. 'ContributorHighlight'",
          "insight_type": "string — e.g. 'focus', 'pattern', 'anomaly'",
          "data_type": "string — 'contributor'",
          "target_entity": "string — 'person'",
          "impact_area": "string — e.g. 'risk', 'deployment', 'collaboration'",
          "name": "string",
          "activity_summary": "Summarize only if this contributor exceeds 10% of activity or made a unique contribution.",
          "top_items": ["string — PRs, issues, or theme names"],
          "stats": [
            {
              "metric": "string",
              "value": "number",
              "evidence": ["string — e.g. ticket references or metrics"]
            }
          ]
        }
      ]`,
  format: {
    type: "json",
    fields: ["name", "activity_summary"],
    constraints: [
      { field: "name", type: "string", required: true },
      { field: "activity_summary", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as ContributorData[];
          return (
            Array.isArray(parsed) &&
            parsed.every((item) => item.name && item.activity_summary)
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// NotableContributors Block Parser
function notableContributorsParser(data: any) {
  try {
    return {
      data: data[notableContributorsBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse notable contributors data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// NotableContributors Block Variants
const notableContributorsVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard contributor display",
  },
  {
    id: "profile",
    name: "Profile",
    description: "Profile-focused contributor display",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal contributor display",
  },
];

// NotableContributors Block Renderer
function NotableContributorsBlockRenderer(props: any) {
  const { data, variant, settings, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const contributorsData = data[notableContributorsBlock.id];

  return (
    <>
      {contributorsData?.length > 0 && (
        <div className="vzbl-panel-container">
          <Typography.Title level={4} className="vzbl-header-4">
            Notable Contributors
          </Typography.Title>
          <div className="responsive-card-grid">
            {contributorsData.map(
              (contributor: ContributorData, idx: number) => (
                <div
                  key={idx}
                  style={{ height: "100%" }}
                  className="vzbl-panel-shadow vzbl-panel-overlay"
                >
                  <Contributor
                    name={contributor.name}
                    activitySummary={contributor.activity_summary}
                    impactArea={contributor.impact_area}
                    topItems={contributor.top_items}
                    stats={contributor.stats}
                    variant={variant ?? "profile"}
                    className="vzbl-panel-shadow vzbl-panel-overlay"
                  />
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Create NotableContributors Block
const notableContributorsBlock = createBaseBlock({
  id: "notable-contributors",
  name: "Notable Contributors Block",
  description: "Displays notable contributors and their impact",
  recipe: notableContributorsRecipe,
  variants: notableContributorsVariants,
  parser: notableContributorsParser,
  renderer: NotableContributorsBlockRenderer,
});

// Export the block for direct use
export { notableContributorsBlock };
